/**
 * **The two reads a survey report stands on, and nothing built from them.**
 *
 * `GET /customer/{id}/surveys` is the whole history — up to 200 occurrences of every form, with
 * each one's FROZEN question snapshot — and beside it the lean exercise batch that names an
 * `ExerciseClip` question. Both surfaces that draw a report need exactly this pair, so the pair
 * lives once and each surface projects it for itself.
 *
 * ── It waits for BOTH, and only for the ones it MADE ─────────────────────────────────────────
 * A report drawn from the surveys alone would name a clip row with the trainer's free text and
 * re-name it a moment later — a partial answer wearing the shape of a complete one. The exercise
 * batch is disabled when no visible form has a clip question, and `useExerciseNames` is where that
 * flag's trap — a DISABLED query is `isPending` for ever — is handled.
 *
 * ── Every hook call is IN HERE, so the caller's build compiles to one guarded scope ───────────
 * `buildSurveyHistory` is the expensive call on both surfaces: it walks every question of every
 * visible occurrence and mints a cell for each. The React Compiler cannot memoise a chain that is
 * interleaved with later hook calls, so a caller that finishes its hooks by calling this one and
 * then builds gets a scope keyed on what the build actually depends on. `pages/client-detail`'s
 * `useSurveyHistory` header carries the measurement that rule came from.
 *
 * The source comes through the query's own `select`, which is the other half: TanStack keeps the
 * selected value structurally shared across refetches, so an answer that did not change hands back
 * the same object rather than a fresh mapping of identical rows.
 */

import { useQuery } from '@tanstack/react-query';

import { useExerciseNames } from '~/entities/exercise-library';

import { clientSurveysQueryOptions } from '../api/surveyHistoryQueries';
import { toSurveyHistorySource } from '../api/surveyHistorySource';
import type { SurveyHistorySource } from '../model/surveyHistory';

export interface SurveyHistorySourceState {
  /** Every occurrence, narrowed. `null` until the read lands, and after one that did not. */
  readonly source: SurveyHistorySource | null;
  /** `exerciseId` → name, for an `ExerciseClip` question and for a record goal's lift. */
  readonly exerciseNames: ReadonlyMap<number, string>;
  /** Either read is still in flight. ONE flag, because a report is one answer. */
  readonly pending: boolean;
  readonly failed: boolean;
  /** Read the surveys again — a surface's „Spróbuj ponownie" after `failed`. */
  readonly retry: () => void;
}

/**
 * `enabled` is the task card's on-screen gate; the report surfaces leave it out. A disabled read is
 * `isPending` for ever, which is exactly the wait a card that has not been looked at yet shows.
 */
export function useSurveyHistorySource(clientId: number, enabledArg?: boolean): SurveyHistorySourceState {
  const enabled = enabledArg ?? true;
  const surveys = useQuery({
    ...clientSurveysQueryOptions(clientId),
    enabled,
    select: toSurveyHistorySource,
  });
  const source = surveys.data ?? null;

  const exerciseIds = (source?.occurrences ?? []).flatMap((occurrence) => [
    ...occurrence.questions.flatMap((question) =>
      question.answerType === 'ExerciseClip' && question.exerciseId !== null
        ? [question.exerciseId]
        : [],
    ),
    // A record goal set in the intake names its lift by id too.
    ...occurrence.answers.flatMap((answer) =>
      answer.goal?.exerciseId == null ? [] : [answer.goal.exerciseId],
    ),
  ]);
  const exercises = useExerciseNames(exerciseIds);

  return {
    source,
    exerciseNames: exercises.names,
    // A RETRY is a wait too (`src/shared/AGENTS.md`): an errored query keeps `isPending: false`
    // while it refetches, so the gate would never reopen and the answer would cut in unannounced.
    pending:
      surveys.isPending ||
      (surveys.isError && surveys.isFetching && surveys.data === undefined) ||
      exercises.pending,
    failed: surveys.isError || exercises.failed,
    retry: () => {
      void surveys.refetch();
    },
  };
}
